import React, {useContext} from 'react';
import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import {results} from "../apis/calculationApi";
import {Alert, Button, CircularProgress, LinearProgress, Paper, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {DataGrid} from "@mui/x-data-grid";
import GetAppTwoToneIcon from '@mui/icons-material/GetAppTwoTone';
import * as XLSX from "xlsx";
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import {LanguageContext} from "../components/LanguageContext";
import {useDataGridLocaleText} from "../utils/dataGridLocale";

const exportToExcel = (data, fileName) => {
    const headers = Object.keys(data[0]);
    const exportData = [
        headers,
        ...data.map((obj) => headers.map((header) => obj[header])),
    ];
    const ws = XLSX.utils.aoa_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, fileName + ".xlsx");
};

const CalculationResultPage = () => {
    const {id} = useParams();
    const {translations} = useContext(LanguageContext);
    const dgLocale = useDataGridLocaleText();

    const {data, isLoading, isError} = useQuery({
        queryKey: ['results', id],
        queryFn: () => results(id)
    });

    const columns = [
        {
            field: "id",
            headerName: translations.calcId,
            minWidth: 50,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "article_code",
            headerName: translations.resultsColCode,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "article_name",
            headerName: translations.resultsColName,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "abc",
            headerName: translations.resultsColAbc,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "xyz",
            headerName: translations.resultsColXyz,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "ved",
            headerName: translations.resultsColVed,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "safety_stock",
            headerName: translations.resultsColSafety,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "rop",
            headerName: translations.resultsColRop,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "forecast",
            headerName: translations.resultsColForecast,
            minWidth: 150,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
    ];

    return (
        <Grid container spacing={2}>
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center"}}>
                    <Typography sx={{fontSize: 22, textAlign: "center"}}>
                        <InfoTwoToneIcon size={22} sx={{mr: 2}}/>
                        {translations.resultsTitle}
                    </Typography>
                </Paper>
            </Grid>
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center"}}>
                    <Button
                        disabled={isLoading}
                        variant="contained"
                        fullWidth
                        startIcon={isLoading ? null : <MicrosoftIcon/>}
                        endIcon={isLoading ? null : <GetAppTwoToneIcon/>}
                        onClick={() => {
                            const date = new Date();
                            exportToExcel(
                                data?.data,
                                "KaproSCM_Results_" + date.getDate() + "_" + (date.getMonth() + 1) + "_" + date.getFullYear() + "_" + date.getHours() + "_" + date.getMinutes() + "_" + date.getSeconds()
                            );
                        }}
                    >
                        {isLoading ? <CircularProgress size={25} color="inherit"/> : translations.resultsExport}
                    </Button>
                </Paper>
            </Grid>
            <Grid size={{xs: 12}}>
                <Paper sx={{p: 2, justifyContent: "center", alignItems: "center", height: 800}}>
                    {isError ? (
                        <Alert severity="error">
                            {translations.resultsErrorLoad}
                        </Alert>
                    ) : isLoading ? (
                        <LinearProgress size={24} color="inherit"/>
                    ) : (
                        <DataGrid
                            columns={columns}
                            rows={isLoading ? [] : data?.data}
                            pageSizeOptions={[10, 25, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: {pageSize: 25, page: 0},
                                },
                            }}
                            localeText={dgLocale}
                        />
                    )}
                </Paper>
            </Grid>
        </Grid>
    );
};

export default CalculationResultPage;
